import { combine, createEffect, createEvent, createStore, sample } from 'effector';
import { z } from 'zod';
import { detectLocale, LOCALES, type Locale } from '../../shared/lib/locale';
import { readStored, writeStored } from '../../shared/lib/storage';

export const THEME_PREFERENCES = ['dark', 'light', 'system'] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];
export type ResolvedTheme = 'dark' | 'light';

/** Also read by the pre-paint script in index.html — keep the key and shape in sync. */
export const PREFERENCES_STORAGE_KEY = 'finsim:preferences';

const storedSchema = z.object({
  theme: z.enum(THEME_PREFERENCES).optional().catch(undefined),
  locale: z.enum(LOCALES).optional().catch(undefined),
});

export interface Preferences {
  theme: ThemePreference;
  locale: Locale;
}

/** Reads persisted preferences, falling back to the browser language and the system theme. */
export function readPreferences(browserLanguages: readonly string[]): Preferences {
  const stored = readStored(PREFERENCES_STORAGE_KEY, storedSchema, {});
  return {
    theme: stored.theme ?? 'system',
    locale: stored.locale ?? detectLocale(browserLanguages),
  };
}

export const preferencesLoaded = createEvent<Preferences>();
export const themeChanged = createEvent<ThemePreference>();
/** The user picked a language: shown and saved. */
export const localeChanged = createEvent<Locale>();
/** A link asked for a language (`?lang=`): shown for this visit, never saved. */
export const localeOverridden = createEvent<Locale>();
export const systemThemeChanged = createEvent<ResolvedTheme>();

export const $theme = createStore<ThemePreference>('system')
  .on(preferencesLoaded, (_, p) => p.theme)
  .on(themeChanged, (_, theme) => theme);

/** The language shown. */
export const $locale = createStore<Locale>('en')
  .on(preferencesLoaded, (_, p) => p.locale)
  .on([localeChanged, localeOverridden], (_, locale) => locale);

/** The language saved as the preference (a link's language doesn't change it). */
const $savedLocale = createStore<Locale>('en')
  .on(preferencesLoaded, (_, p) => p.locale)
  .on(localeChanged, (_, locale) => locale);

export const $systemTheme = createStore<ResolvedTheme>('dark').on(
  systemThemeChanged,
  (_, theme) => theme,
);

export const $resolvedTheme = combine($theme, $systemTheme, (theme, system): ResolvedTheme =>
  theme === 'system' ? system : theme,
);

export const savePreferencesFx = createEffect((prefs: Preferences) => {
  writeStored(PREFERENCES_STORAGE_KEY, prefs);
});

sample({
  clock: [themeChanged, localeChanged],
  source: { theme: $theme, locale: $savedLocale },
  target: savePreferencesFx,
});
